<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isNew && !isChanged"
        @click="save"
      >
        <v-icon>
          mdi-check
        </v-icon>
        {{ $t('button.save_changes') }}
      </v-btn>
      <v-btn
        v-if="!isNew && !isDefault"
        class="mr-2"
        color="error"
        @click="deleteConfirmation = true"
      >
        <v-icon>
          mdi-delete
        </v-icon>
        <span class="d-none d-lg-block">{{ $t('button.delete') }}</span>
      </v-btn>
    </template>
    <template #content>
      <!-- Template Editor Dialog -->
      <message-center-template-dialog
        v-if="templateEditDialog"
        :visible="templateEditDialog"
        :template="template"
        :rule="r"
        @update-template="updateTemplate"
        @close="templateEditDialog = false"
      />
      <!-- Template Preview Dialog -->
      <message-center-template-preview-dialog
        v-if="templatePreviewDialog && previewParams"
        :message-type-id="previewParams.messageTypeId"
        :event-type-id="previewParams.eventTypeId"
        :locale="previewParams.locale"
        :subject="previewParams.subject"
        :body="previewParams.body"
        :title="previewParams.title"
        :service-id="previewParams.serviceId"
        @close="templatePreviewDialog = false"
      />
      <!--  -->
      <confirmation-dialog
        :visible="deleteConfirmation"
        :message="$t('message.confirm_delete_message_rule')"
        @confirm="remove"
        @close="deleteConfirmation = false"
      />

      <validation-observer ref="observer">
        <settings-group
          :title="$t('settings.messagecenter.rule_editor.rule_settings')"
          :subtitle="$t('settings.messagecenter.rule_editor.rule_settings_subtitle')"
        >
          <v-col
            class="py-0"
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                v-model="r.messageType"
                :disabled="!isNew"
                :items="messageTypes"
                return-object
                :label="$t('title.message_type')"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <!-- Event Type -->
          <v-col
            :disabled="isDefault || !isNew"
            class="py-0"
            cols="12"
            md="8"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                v-model="r.eventType"
                :disabled="isDefault || !isNew"
                :items="eventTypes"
                return-object
                :label="$t('label.event_type')"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <!-- Active -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-switch
              v-model="r.isInactive"
              :false-value="true"
              :true-value="false"
              hide-details
              :label="$t('label.active')"
            />
            <p class="pt-0 mt-0 ml-12">
              {{ $t('label.message_center_disable_rule') }}
            </p>
          </v-col>
          <!-- Send BCC -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-switch
              v-if="sendBccAllowed"
              v-model="r.sendBcc"
              class="pt-0 mt-0"
              hide-details
              :label="$t('label.send_bcc')"
            />
            <p
              v-if="sendBccAllowed"
              class="pt-0 mt-0 ml-12"
            >
              {{ $t('label.message_center_bcc_rule') }}
            </p>
          </v-col>
        </settings-group>

        <settings-group
          v-if="r.eventType && r.eventType.isTimed && !isNew"
          :title="$t('settings.messagecenter.rule_editor.rule_schedule')"
          :subtitle="$t('settings.messagecenter.rule_editor.rule_schedule_subtitle')"
        >
          <!-- Send days in advance -->
          <v-col
            v-if="r.eventType.isTimedBefore"
            cols="12"
            sm="6"
            md="4"
            class="pb-0 pt-0"
          >
            <v-select
              v-model="r.sendDaysInAdvance"
              :items="daysAdvance"
              :label="$t('label.send_days_in_advance')"
            />
          </v-col>
          <!-- Send Days After -->
          <v-col
            v-if="r.eventType.isTimedAfter"
            cols="12"
            sm="6"
            md="4"
            class="pb-0 pt-0"
          >
            <v-select
              v-model="r.sendDaysAfter"
              :items="daysAfter"
              :label="$t('label.send_days_after')"
            />
          </v-col>
          <!-- Time Picker -->
          <v-col
            cols="12"
            sm="6"
            md="4"
            class="pb-0 pt-0"
          >
            <time-picker-field
              v-if="r.eventType && (r.eventType.isTimedAfter || r.eventType.isTimedBefore)"
              v-model="r.sendAtSpecificTime"
              :label="$t('label.send_at_time')"
            />
          </v-col>
        </settings-group>

        <settings-group
          v-if="!r.isDefault"
          gutter
          :title="$t('settings.messagecenter.rule_editor.advanced_filtering')"
          :subtitle="$t('settings.messagecenter.rule_editor.advanced_filtering_subtitle')"
        >
          <!-- Campaign -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-select
              :value="r.campaign"
              :items="campaigns"
              return-object
              :label="$t('label.campaign')"
              @change="updateCampaign"
            />
          </v-col>
          <!-- Service -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-select
              :value="r.service"
              :items="services"
              return-object
              :label="$t('label.service')"
              @change="updateService"
            />
          </v-col>
          <!-- Section -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-select
              :value="r.tab"
              :items="sections"
              return-object
              :label="$t('label.section')"
              @change="updateTab"
            />
          </v-col>
        </settings-group>

        <settings-group
          v-if="!isNew"
          gutter
          :title="$i18n.tc('label.message_templates')"
          :subtitle="$t('settings.messagecenter.rule_editor.custom_templates_subtitle')"
        >
          <v-col
            cols="12"
            class="pt-0"
          >
            <v-data-table
              class="clickable-table"
              :headers="headers"
              :items="templates"
              disable-pagination
              xhide-default-header
              hide-default-footer
              mobile-breakpoint="0"
              :item-class="() => 'text-overline'"
              :no-data-text="$t('label.no_custom_templates')"
              item-key="enabledLocale"
              @click:row="editTemplate"
            >
              <template #item.language="props">
                {{ localesByLocale.get(props.item.enabledLocale)?.language || props.item.enabledLocale }}
              </template>
              <template #item.flags="props">
                <v-chip
                  v-if="!props.item.isExactMatch"
                  x-small
                  color="gray"
                >
                  {{ $t('label.default') }}
                </v-chip>
                <v-chip
                  v-else
                  x-small
                  color="success"
                >
                  {{ $t('label.custom') }}
                </v-chip>
              </template>
              <template #item.actions="props">
                <v-btn
                  icon
                  small
                  :disabled="!props.item.body"
                  :title="$t('label.preview')"
                  @click.stop="previewTemplate(props.item)"
                >
                  <v-icon small>
                    mdi-eye
                  </v-icon>
                </v-btn>
                <v-btn
                  :disabled="!props.item.isExactMatch"
                  icon
                  @click.stop="removeTemplate(props.item)"
                >
                  <v-icon small>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </template>
            </v-data-table>
            <template v-if="isNewMcEditing">
              <v-divider class="my-4" />
              <p class="text-center">
                {{ $t('settings.messagecenter.rule_editor.add_or_remove_languages') }}
              </p>
            </template>
          </v-col>
          <!-- new template button -->
          <v-col
            v-if="!isNew && !isNewMcEditing"
            class="pt-0 d-flex justify-center"
            cols="12"
          >
            <v-btn
              outlined
              color="primary"
              @click="addTemplate"
            >
              <v-icon>
                mdi-plus
              </v-icon>
              {{ $t('button.add_custom_template') }}
            </v-btn>
          </v-col>
        </settings-group>

        <settings-group
          v-if="obsoleteTemplates.length > 0"
          gutter
          :title="isLocalePriorityDraftMode ? $i18n.tc('label.currently_live_templates') : $i18n.tc('label.obsolete_templates')"
          :subtitle="isLocalePriorityDraftMode
            ? $t('settings.messagecenter.rule_editor.currently_live_templates_subtitle')
            : $t('settings.messagecenter.rule_editor.obsolete_templates_subtitle')"
        >
          <v-col
            cols="12"
            class="pt-0"
          >
            <v-data-table
              class="clickable-table"
              :headers="headers"
              :items="obsoleteTemplates"
              disable-pagination
              hide-default-footer
              mobile-breakpoint="0"
              :item-class="() => 'text-overline'"
              item-key="locale"
              @click:row="editTemplate"
            >
              <template #item.language="props">
                {{ localesByLocale.get(props.item.locale)?.language || props.item.locale }}
              </template>
              <template #item.flags>
                <v-chip
                  x-small
                  :color="isLocalePriorityDraftMode ? 'info' : 'error'"
                >
                  {{ isLocalePriorityDraftMode ? $t('label.currently_live') : $t('label.obsolete') }}
                </v-chip>
              </template>
              <template #item.actions="props">
                <v-btn
                  v-if="!isLocalePriorityDraftMode"
                  icon
                  @click.stop="removeObsoleteTemplate(props.item)"
                >
                  <v-icon small>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-col>
        </settings-group>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script lang="ts">
/* eslint-disable */

import Vue from 'vue';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import UnsavedGuard from '@/mixins/unsaved-guard';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import MessageCenterTemplateDialog from '@/components/views/settings/MessageCenterTemplateDialog.vue';
import MessageCenterTemplatePreviewDialog from '@/components/views/settings/MessageCenterTemplatePreviewDialog.vue';
import { strcmp } from '@/services/common';
import { actionShowError, performAction, performDispatchAction, performSaveAction } from '@/services/vue-utils';
import { cloneModel, isModelEqualToModel, uniqueMinId } from '@/model/model-utils';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import MessageCenterStore from '@/store/message-center-store';
import MessageTemplate, { TemplatePriority } from '@/grpc-api/model/message-template';
import MessageRule from '@/grpc-api/model/message-rule';
import MessageType from '@/grpc-api/model/message-type';
import { MessageTypeChannel } from '@/model/MessageType';
import Campaign from '@/grpc-api/model/campaign';
import ReservationService from '@/grpc-api/model/reservation-service';
import Tab from '@/grpc-api/model/tab';
import EventType from '@/grpc-api/model/event-type';
import Locale from '@/model/Locale';
import router from '@/router';

export default Vue.extend({
  name: 'MessageCenterRuleEditor',
  components: {
    ConfirmationDialog, SettingsForm, MessageCenterTemplateDialog, MessageCenterTemplatePreviewDialog,
  },
  mixins: [UnsavedGuard, SettingsBreadcrumbs],

  data() {
    return {
      deleteConfirmation: false,
      r: new MessageRule(),
      oldr: new MessageRule(),
      template: new MessageTemplate(),
      breadcrumbs: [] as any,
      daysAdvance: [...new Array(31)].map(
        (e, i) => (
          { value: i, text: i === 0 ? this.$i18n.t('label.same_day') as string : this.$i18n.tc('label.x_days', i) }),
      ),
      daysAfter: [...new Array(31)].map(
        (e, i) => {
          let text = '';
          if (i === 0) {
            text = this.$i18n.t('label.same_day') as string;
          } else if (i === 1) {
            text = this.$i18n.t('label.next_day') as string;
          } else {
            text = this.$i18n.tc('label.x_days', i);
          }
          return { value: i, text };
        },
      ),
      headers: [
        {
          text: this.$i18n.t('label.language'),
          value: 'language',
          align: 'start',
          sortable: false,
          width: '100%'
        },
        {
          value: 'flags',
          align: 'right',
          sortable: false,
        },
        {
          value: 'actions',
          align: 'right',
          sortable: false,
          width: '100px'
        },
      ],
      templateEditDialog: false,
      templatePreviewDialog: false,
      previewParams: null as null | { messageTypeId: number, eventTypeId: number, locale: string, subject: string, body: string, title: string, serviceId: number },
    };
  },
  computed: {
    isChanged(): boolean {
      return !isModelEqualToModel(this.r, this.oldr);
    },
    isNew(): boolean {
      return this.r.id === 0;
    },
    isDefault(): boolean {
      return this.r.isDefault === true;
    },
    messageTypes(): MessageType[] {
      return this.$tstore.getters.messageCenterMessageTypes;
    },
   isLocalePriorityMode(): boolean {
      return this.$tstore.getters.messageCenterIsLocalePriorityMode as boolean;
    },
    isLocalePriorityDraftMode(): boolean {
      return this.$tstore.getters.messageCenterIsLocalePriorityDraftMode as boolean;
    },
    // during the draft step the editor behaves exactly like locale priority mode:
    // editable new templates + read-only "currently live" templates (shown as obsolete)
    isNewMcEditing(): boolean {
      return this.isLocalePriorityMode || this.isLocalePriorityDraftMode;
    },
    campaigns(): ({ text: string, value: undefined } | { divider?: boolean } | Campaign)[] {
      return [
        { text: this.$i18n.tc('label.all_campaigns'), value: undefined },
        { divider: true },
        ...this.$tstore.state.messageCenter.campaigns ?? [],
      ];
    },
    services(): ({ text: string, value: undefined } | { divider?: boolean } | ReservationService)[] {
      return [
        { text: this.$i18n.tc('label.all_services'), value: undefined },
        { divider: true },
        ...this.$tstore.state.messageCenter.services ?? [],
      ];
    },
    sections(): ({ text: string, value: undefined } | { divider?: boolean } | Tab)[] {
      return [
        { text: this.$i18n.tc('label.all_sections'), value: undefined },
        { divider: true },
        ...this.$tstore.state.settings.tabs ?? [],
      ];
    },
    eventTypes(): EventType[] {
      return this.$tstore.state.messageCenter.eventTypes;
    },
    localesByLocale(): Map<string, Locale> { return this.$tstore.getters.localesByLocale; },
    sortedLocales(): Locale[] {
      const enabledLocales = this.$tstore.getters.messageCenterEnabledLocales as string[];
      const localesEntities = enabledLocales.flatMap((locale) => this.localesByLocale.get(locale)!);
      const sortedLocalesEntities = localesEntities.sort((a, b) => strcmp(a.language, b.language));
      return sortedLocalesEntities;
    },
    templates(): MessageTemplate[] {
      if (!this.isNewMcEditing) {
        // old behavior
        return this.r.templates.filter((o) => !o.isDeleted);
      }

      const templatesByEnabledLocale = new Map<string, MessageTemplate>();
      this.r.templates.forEach((o) => {
        if (o.isDeleted || o.enabledLocale === undefined) return;
        const et = templatesByEnabledLocale.get(o.enabledLocale);
        if (!et || o.priority < et.priority || (o.priority === et.priority && o.rank < et.rank)) {
          templatesByEnabledLocale.set(o.enabledLocale, o);
        }
      });

      return this.sortedLocales.map((locale) => {
        let t = templatesByEnabledLocale.get(locale.locale);
        if (!t) {
          t = new MessageTemplate();
          t.enabledLocale = locale.locale;
          t.priority = TemplatePriority.MISSING;
        }
        return t;
      });
    },
    obsoleteTemplates(): MessageTemplate[] {
      if (!this.isNewMcEditing) return []; // no obsolete templates in old mode
      return this.r.obsoleteTemplates.filter((o) => !o.isDeleted);
    },
    sendBccAllowed(): boolean {
      return this.r.messageType?.channel === MessageTypeChannel.EMAIL || false;
    },
  },
  created() {
    loadStoreModule(this, 'messageCenter', MessageCenterStore, async () => { this.load(); });
  },
  beforeDestroy() {
    unloadStoreModule(this, 'messageCenter', async () => { this.$tstore.dispatch('resetMessageCenter'); });
  },
  // mounted() {
  //   this.reset();
  //   this.breadcrumbs[this.breadcrumbs.length - 1].text = this.r.eventType;
  // },
  watch: {
    r: {
      deep: true,
      handler(value: boolean, oldValue: boolean) {
        console.log('Rule changed', value, oldValue);
      },
    },
  },
  methods: {
    async load() {

      const id = Number(this.$route.params.id) || 0;

      // empty new rule
      this.oldr = new MessageRule();
      this.r = cloneModel(this.oldr);

      // set breadcrumbs
      this.breadcrumbs[this.breadcrumbs.length - 1].text = id === 0
        ? this.$i18n.t('title.new_message_rule')
        : this.$i18n.t('title.message_rule');

      // load message center if not loaded
      if (!this.$tstore.getters.isMessageCenterLoaded) {
        const ok = await performAction(
          null,
          this.$i18n.tc('error.load_message_center'),
          async () => {
            await this.$tstore.dispatch('loadMessageCenter');
          }
        );
        if (!ok) {
          this.$router.replace('/settings/message-center');
          return;
        }
      }

      // new rule requested
      if (id === 0) return;

      // load existing rule if needed
      const rules: MessageRule[] = (id < 0 ? this.$tstore.getters.messageCenterBuiltinRules : this.$tstore.state.messageCenter.rules) ?? [];
      let rule: MessageRule | undefined | null = rules.find((o: MessageRule) => o.id === id);
      if (rule && rule.templates.length === 0) rule = await performDispatchAction<MessageRule>('messageCenterGetRule', { id: rule.id });
      if (!rule) {
        router.replace('/settings/message-center');
        return;
      }

      // set rule
      this.oldr = rule;
      this.r = cloneModel(this.oldr);

      // set breadcrumbs
      this.breadcrumbs[this.breadcrumbs.length - 1].text = this.r.eventType;
    },
    async save() {
      if (this.isNew) {
        const rules = [...this.$tstore.getters.messageCenterBuiltinRules, ...this.$tstore.state.messageCenter.rules] as MessageRule[];
        const exists = rules.some((o) => MessageRule.compare(o, this.r) === 0);
        if (exists) {
          actionShowError(new Error(this.$i18n.tc('error.rule_already_exists')));
          return;
        }
      }

      let rule: MessageRule | undefined;
      await performSaveAction(
        this.$refs.observer,
        async () => {
          rule = await this.$tstore.dispatch('messageCenterUpdateRule', { rule: this.r });
        },
      );

      if (!rule) return;

      // set rule
      this.oldr = rule;
      this.r = cloneModel(this.oldr);

      // set breadcrumbs
      this.breadcrumbs[this.breadcrumbs.length - 1].text = this.r.eventType;

      // if (!this.isNew) this.$router.back();
    },
    async remove() {
      let rule: MessageRule | undefined;
      await performAction(
        this.$tc('message.success_message_rule_deleted'),
        this.$tc('error.deleting_message_rule'),
        async () => {
          rule = await this.$tstore.dispatch('messageCenterUpdateRule', { rule: this.r, delete: true });
        },
      );

      if (!rule) return;

      this.oldr = rule;
      this.r = cloneModel(this.oldr);

      this.$router.back();
    },
    editTemplate(template: MessageTemplate) {
      if (!template.isExactMatch) {
        template = cloneModel(template);
        template.id = 0; // new template
        template.locale = template.enabledLocale;
        template.layoutId = template.layoutId;
      }

      this.template = template;
      this.templateEditDialog = true;
    },
    previewTemplate(template: MessageTemplate) {
      // The row reflects the current (possibly unsaved) editor state held in r.templates, so
      // we send its content directly; the backend renders it as the rule's message/event type
      // in this locale (and derives the layout server-side).
      const locale = template.enabledLocale || template.locale || '';
      const lang = this.localesByLocale.get(locale)?.language;
      this.previewParams = {
        messageTypeId: this.r.messageType?.id ?? 0,
        eventTypeId: this.r.eventType?.id ?? 0,
        locale,
        subject: template.subject ?? '',
        body: template.body ?? '',
        title: [this.r.eventType?.text, lang].filter((f) => f !== undefined && f !== '').join(': '),
        serviceId: this.r.service?.id ?? 0,
      };
      this.templatePreviewDialog = true;
    },
    addTemplate() {
      this.template = new MessageTemplate();
      this.templateEditDialog = true;
    },
    removeTemplate(template: MessageTemplate) {
      // multiple templates can have the same id
      if (template.id < 0) this.r.templates = this.r.templates.filter((o) => o.id !== template.id);
      else this.r.templates = this.r.templates.map((o) => {
        if (o.id === template.id) o.isDeleted = true;
        return o;
      });
    },
    removeObsoleteTemplate(template: MessageTemplate) {
      this.r.obsoleteTemplates = this.r.obsoleteTemplates.map((o) => {
        if (o.id === template.id) o.isDeleted = true;
        return o;
      });
    },
    updateTemplate(template: MessageTemplate) {
      if (template.id !== 0) {
        // existing template -> update all matching templates
        this.r.templates = this.r.templates.map((o) => {
          if (o.id === template.id) {
            o.body = template.body;
            o.subject = template.subject;
          }
          return o;
        });
      } else {
        // new template -> add the exact-match template
        template.id = uniqueMinId(this.r.templates);
        template.rank = -1;
        if (!this.isNewMcEditing) {
          // old behavior -> single template for the chosen locale, no language-match fallbacks
          const t = cloneModel(template);
          t.enabledLocale = template.locale;
          t.priority = TemplatePriority.LOCALE_MATCH;
          t.isExactMatch = true;
          this.r.templates.push(t);
        } else {
          // new MC editing -> also add language-match fallbacks so every enabled
          // locale resolves to this template (display only; not persisted on save)
          this.localesByLocale.forEach((_, locale) => {
            if (template.locale === locale) {
              const t = cloneModel(template);
              t.enabledLocale = locale;
              t.priority = TemplatePriority.LOCALE_MATCH;
              t.isExactMatch = true;
              this.r.templates.push(t);
            } else if (template.locale === locale.substring(0, 2)) {
              const t = cloneModel(template);
              t.enabledLocale = locale;
              t.priority = TemplatePriority.LANGUAGE_MATCH;
              this.r.templates.push(t);
            }
          });
        }
      };
      this.templateEditDialog = false;
    },
    updateCampaign(campaign: Campaign | undefined) {
      this.r.campaign = campaign;
    },
    updateService(service: ReservationService | undefined) {
      this.r.service = service;
    },
    updateTab(tab: Tab | undefined) {
      this.r.tab = tab;
    },
  },
});
</script>

<style lang="css">
.custom-template-row {
  border-bottom: 1px red solid !important;
}
</style>
